import { useQuery, useMutation, useTransport, createConnectQueryKey } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { useMemo } from 'react'

import { listNotes, createNote, updateNote, getNote } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListNotesRequestSchema, GetNoteResponseSchema } from '@/gen/cortado/v1/cortado_pb'
import type { ListNotesResponse } from '@/gen/cortado/v1/cortado_pb'
import { organizationStore } from './organization.store'
import { dateToTimestamp } from '@/lib/date-utils'
import { isValidUUID } from '@/lib/uuid'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'

export function useNotes(options?: {
  parentType?: ParentTypeEnum
  parentId?: string
  jobId?: string
  since?: Date | Timestamp
  limit?: number
  offset?: number
  enabled?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(() => {
    return create(ListNotesRequestSchema, {
      organizationId: selectedOrganizationId || '',
      parentType: options?.parentType,
      parentId: options?.parentId,
      jobId: options?.jobId,
      since: options?.since ? (options.since instanceof Date ? dateToTimestamp(options.since) : options.since) : undefined,
      limit: options?.limit,
      offset: options?.offset,
    })
  }, [selectedOrganizationId, options?.parentType, options?.parentId, options?.jobId, options?.since, options?.limit, options?.offset])

  const query = useQuery(
    listNotes,
    request,
    {
      enabled: !!selectedOrganizationId && (options?.enabled ?? true) && (!options?.parentId || isValidUUID(options.parentId)) && (!options?.jobId || isValidUUID(options.jobId)),
    }
  )

  return {
    notes: query.data?.notes || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to create a new note
 *
 * This hook:
 * - Creates a new note for a specified parent (inquiry, contact, booking, or event)
 * - Automatically invalidates list queries after creation
 * - Returns the created note with full details
 *
 * @returns Mutation result with isLoading, isError, and error states
 */
export function useCreateNote() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createNote,
    {
      onSuccess: (response) => {
        const createdNote = response.note

        if (!createdNote) return

        // Invalidate all note list queries (we can't know all possible filter combinations)
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listNotes,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to update note data
 *
 * This hook:
 * - Provides mutation function to update note fields
 * - Uses field mask to update only specified fields
 * - Updates cache directly with mutation response
 * - Invalidates list queries after update
 *
 * @returns Mutation result with full mutation access
 */
export function useUpdateNote() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const listNotesKey = createConnectQueryKey({
    schema: listNotes,
    transport,
    cardinality: undefined,
  })

  const mutation = useMutation(updateNote, {
    onMutate: async () => {
      // Cancel in-flight list queries so they don't overwrite updates
      await queryClient.cancelQueries({ queryKey: listNotesKey })

      // Snapshot all list caches for rollback
      const previousLists = queryClient.getQueriesData<ListNotesResponse>({
        queryKey: listNotesKey,
      })

      return { previousLists }
    },

    onError: (_error, _variables, context) => {
      // Rollback all list caches to their pre-mutation state
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data)
        }
      }
    },

    onSuccess: (response) => {
      const updatedNote = response.note
      if (!updatedNote) return

      // Update individual note cache
      const queryKey = createConnectQueryKey({
        schema: getNote,
        transport,
        input: { id: updatedNote.id },
        cardinality: 'finite',
      })
      queryClient.setQueryData(
        queryKey,
        create(GetNoteResponseSchema, { note: updatedNote })
      )

      // Update all list caches with the response data
      const existingLists = queryClient.getQueriesData<ListNotesResponse>({
        queryKey: listNotesKey,
      })
      for (const [key, data] of existingLists) {
        if (!data?.notes) continue
        queryClient.setQueryData(key, {
          ...data,
          notes: data.notes.map((note) =>
            note.id === updatedNote.id ? { ...note, ...updatedNote } : note
          ),
        })
      }
    },

    onSettled: () => {
      // Refetch lists in background for eventual consistency
      queryClient.invalidateQueries({ queryKey: listNotesKey })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}
